import React, { useEffect, useId, useRef } from 'react';
import ReactDOM from 'react-dom';
import useFocusTrap from '../../hooks/useFocusTrap';
import './Modal.css';

/**
 * Modal — accessible dialog rendered via React Portal.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - title: string (also used for aria-labelledby)
 *  - children: ReactNode (body content)
 *  - size: 'sm' | 'md' | 'lg' (default 'md')
 *  - initialFocusRef: optional ref to focus on open
 *  - describedBy: optional id for aria-describedby
 *  - footer: optional ReactNode rendered in a sticky footer slot
 *  - dismissOnOverlayClick: close when the backdrop is clicked (default true);
 *    pass false for forms where a stray click would discard the user's input
 */
export default function Modal({
  open,
  onClose,
  title,
  children,
  size = 'md',
  initialFocusRef,
  describedBy,
  footer,
  dismissOnOverlayClick = true,
}) {
  const dialogRef = useRef(null);
  const overlayRef = useRef(null);
  const titleId = useId();

  // Escape closes
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      // Skip an Escape something inside the dialog already handled (e.g. a
      // widget closing its own popup) so it doesn't also close the dialog.
      if (e.key === 'Escape' && !e.defaultPrevented) onClose && onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useFocusTrap(dialogRef, { active: !!open, initialFocusRef });

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const handleOverlayClick = (e) => {
    if (dismissOnOverlayClick && e.target === overlayRef.current) onClose && onClose();
  };

  const dialogClass = `ui-modal__dialog ui-modal__dialog--${size}`;

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className="ui-modal__overlay"
      onClick={handleOverlayClick}
    >
      <div
        ref={dialogRef}
        className={dialogClass}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
      >
        <div className="ui-modal__header">
          <h2 id={titleId} className="ui-modal__title">{title}</h2>
          <button
            type="button"
            className="ui-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="ui-modal__body">{children}</div>
        {footer && <div className="ui-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
